import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Grid3x3, X, Search } from "lucide-react";

/**
 * Grid de categorias de empresa em ícones arredondados (estilo iFood).
 * Aparece apenas em mobile (<lg). Layout: 2 fileiras × 5 colunas.
 * Última posição é sempre "Ver mais" (abre Sheet com busca por nome).
 * Admin marca `featured=true` para destacar até 9 categorias.
 */
export default function CategoryRail({ citySlug }) {
  const [all, setAll] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/company-categories?active=true").then((r) => {
      const list = r.data || [];
      setAll(list);
      const feats = list.filter((c) => c.featured).slice(0, 9);
      if (feats.length < 9) {
        const extras = list.filter((c) => !c.featured).slice(0, 9 - feats.length);
        setFeatured([...feats, ...extras]);
      } else {
        setFeatured(feats);
      }
    }).catch(() => {});
  }, []);

  const filteredAll = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((c) => c.name?.toLowerCase().includes(q) || c.slug?.toLowerCase().includes(q));
  }, [all, query]);

  if (featured.length === 0) return null;

  const goCategory = (slug, name) => {
    setOpen(false);
    setQuery("");
    const dest = citySlug ? `/discover/${citySlug}` : "/discover/all";
    nav(`${dest}?category=${encodeURIComponent(name)}`);
  };

  const renderCategoryButton = (c, testid) => (
    <button
      key={c.slug}
      onClick={() => goCategory(c.slug, c.name)}
      className="flex flex-col items-center gap-1 focus:outline-none active:scale-95 transition-transform"
      data-testid={testid}
    >
      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100/60 border border-blue-200/60 shadow-sm flex items-center justify-center overflow-hidden">
        {c.icon_url ? (
          <img src={c.icon_url} alt={c.name} className="w-8 h-8 object-contain" loading="lazy"/>
        ) : (
          <span className="text-blue-900 font-display text-lg font-bold">{c.name.charAt(0)}</span>
        )}
      </div>
      <span className="text-[10px] font-semibold text-blue-950 text-center leading-tight line-clamp-2 max-w-[64px]">
        {c.name}
      </span>
    </button>
  );

  return (
    <section className="lg:hidden bg-white px-3 pt-3 pb-2 border-b border-stone-100" data-testid="category-rail">
      <div className="grid grid-cols-5 gap-x-1 gap-y-2.5">
        {featured.slice(0, 9).map((c) => renderCategoryButton(c, `category-rail-${c.slug}`))}

        {/* Ver mais (10º slot — sempre canto inferior direito) */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              className="flex flex-col items-center gap-1 focus:outline-none active:scale-95 transition-transform"
              data-testid="category-rail-more"
            >
              <div className="w-12 h-12 rounded-2xl bg-blue-900 text-white shadow-md flex items-center justify-center">
                <Grid3x3 size={20} strokeWidth={2.2}/>
              </div>
              <span className="text-[10px] font-bold text-blue-900 text-center leading-tight">Ver mais</span>
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-3xl p-0 max-h-[85vh] overflow-hidden flex flex-col">
            <DialogPrimitive.Title className="sr-only">Todas as categorias</DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">Lista completa de categorias de empresas — busque pelo nome.</DialogPrimitive.Description>
            <div className="sticky top-0 bg-white border-b border-stone-200 px-4 py-3 flex items-center justify-between z-10">
              <h2 className="font-display text-lg font-bold text-blue-950">Todas as categorias</h2>
              <button onClick={() => setOpen(false)} className="p-1 text-stone-500 hover:text-stone-900" data-testid="category-rail-close"><X size={20}/></button>
            </div>
            <div className="px-4 py-3 bg-white border-b border-stone-100">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
                <input
                  autoFocus={false}
                  type="text"
                  inputMode="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar categoria pelo nome..."
                  className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900/30 focus:border-blue-900"
                  data-testid="category-search-input"
                />
                {query && (
                  <button
                    onClick={() => setQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-700"
                    data-testid="category-search-clear"
                    aria-label="Limpar"
                  >
                    <X size={14}/>
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {filteredAll.length === 0 ? (
                <p className="text-center text-sm text-stone-500 py-12" data-testid="category-search-empty">
                  Nenhuma categoria encontrada para &quot;{query}&quot;
                </p>
              ) : (
                <div className="grid grid-cols-4 gap-3" data-testid="category-search-results">
                  {filteredAll.map((c) => renderCategoryButton(c, `category-all-${c.slug}`))}
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </section>
  );
}
